import styles from './CreateCalculatedMeasureDialog.module.css';
import React, { useMemo, useState } from 'react';
import { Field, Formik } from 'formik';

import Dialog from 'components/ui/Dialog';
import BngField from 'components/bng/form/BngField';
import BngCheckbox from 'components/bng/form/BngCheckbox';
import { BngCodeMirror } from 'components/bng/form/BngCodeMirror';
import BngSelectSearch from 'components/bng/form/BngSelectSearch';
import BngButton from 'components/bng/ui/BngButton';
import BngIconButton from 'components/bng/ui/BngIconButton';
import bngYup from 'components/bng/form/yup/BngYup';
import Api from 'components/Api';
import UiMsg from 'components/ui/UiMsg';
import useTranslation from 'components/hooks/useTranslation';
import useAsyncEffect from 'components/hooks/useAsyncEffect';
import Icon from 'components/ui/common/Icon';
import Tooltip from 'components/ui/Tooltip';
import OpTextConfirmation from 'components/ui/OpTextConfirmation';
import BngForm from 'components/bng/form/BngForm';

export default function CreateCalculatedMeasureDialog({
  closeModal = _.noop,
  calculatedMeasure,
  calculatedMeasures = [],
  inMemoryId = 0,
  onChange = _.noop,
}) {
  const { t } = useTranslation();

  const [dimensionOpts, setDimensionOpts] = useState([]);
  const [cubeMeasuresOpts, setCubeMeasuresOpts] = useState([]);
  const [formatStringOpts, setFormatStringOpts] = useState([]);
  const [measureIsValid, setMeasureIsValid] = useState(false);
  const [loading, setLoading] = useState(false);

  useAsyncEffect({
    onMount: async () => {
      try {
        const { dimensions, measures } = await Api.Origin.CalculatedMeasure.findStructure(inMemoryId);
        setDimensionOpts(dimensions.filter((d) => !d.disabled));
        setCubeMeasuresOpts(measures);

        const formatStrings = await Api.Analysis.findFormatStrings();
        setFormatStringOpts(
          formatStrings
            .filter((fs) => fs.isFormatString)
            .map((fs) => ({
              ...fs,
              label: t(fs.label),
            }))
        );
      } catch (e) {
        console.error('Error while fetching options', e);
        UiMsg.ajaxError(null, e);
      }
    },
  });

  const tooltipMeasures = useMemo(() => {
    const indent = '  ';
    const physicalList = cubeMeasuresOpts.map((m) => `${indent}• ${m.description} - [Measures].[${m.label}]`);
    const calculateList = calculatedMeasures.map((c) => `${indent}• ${c.caption || c.name} - [Measures].[${c.name}]`);

    return [t('pop.physical.measures'), ...physicalList, '', t('pop.calculated.measures'), ...calculateList].join('\n');
  }, [cubeMeasuresOpts, calculatedMeasures]);

  const validationSchema = useMemo(() => {
    return bngYup((yup) =>
      yup.object({
        name: yup
          .string()
          .required()
          .test({
            test(value) {
              if (_.isEmpty(value)) {
                return false;
              }
              const temp = calculatedMeasures.find((cm) => cm.name === value);
              return temp && temp.id !== calculatedMeasure?.id
                ? this.createError({ message: t('inMemory_nameAlreadyInUse') })
                : true;
            },
          })
          .default(''),
        caption: yup.string().default(''),
        dimension: yup.string().required().default('Measures'),
        cellFormatter: yup.string().default(''),
        visible: yup.bool().default(true),
        dynamic: yup.bool().default(false),
        formula: yup.string().required().default(''),
        formatString: yup.string().default(''),
      })
    );
  }, []);

  const initialValues = useMemo(() => {
    return _.merge(validationSchema.default(), calculatedMeasure);
  }, []);

  const cellFormatOpts = useMemo(() => {
    return [
      {
        value: 'br.com.sol7.formatters.TimeFormatter',
        label: t('cell.formatters.time'),
      },
    ];
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values) => {
        setLoading(true);
        try {
          const handleSave = async () => {
            try {
              const data = _.merge({}, calculatedMeasure, values);
              await Api.Origin.CalculatedMeasure.save(inMemoryId, data);
              onChange();
              closeModal();
              UiMsg.ok(t('InMemoryCalculatedMeasureCreated'));
            } catch (e) {
              console.error('Error while saving calculated measure', e);
              UiMsg.ajaxError(null, e);
            }
          };

          if (measureIsValid) {
            await handleSave();
          } else {
            OpTextConfirmation({
              msg: { t },
              level: 'warning',
              title: t('attention'),
              html: t('save.measure.without.validate'),
              onConfirm: handleSave,
              disableInput: true,
            });
          }
        } catch (e) {
          console.error(`Error while ${calculatedMeasure?.id ? 'updating' : 'saving'} measure!`, e);
          UiMsg.ajaxError(null, e);
        } finally {
          setLoading(false);
        }
      }}
      validationSchema={validationSchema}
    >
      {({ values, setValues, dirty }) => (
        <Dialog
          onClose={closeModal}
          title={`${t('calculated.measures')}: ${calculatedMeasure?.caption || ''}`}
          className={`CreateCalculatedMeasureDialog ${styles.dialog}`}
          loading={loading}
          newDialogLayout={true}
        >
          <BngForm>
            <Dialog.Body>
              <div className={`${styles.blockWrapper}`}>
                <Field
                  name="name"
                  label={t('name')}
                  component={BngField}
                  required={true}
                  disabled={Number.isFinite(calculatedMeasure?.id)}
                />
                <Field name="caption" label={t('display_name')} component={BngField} />
              </div>
              <div className={`${styles.blockWrapper} ${styles.secondRow}`}>
                <Field
                  name="dimension"
                  rootClassName={`dimension`}
                  label={t('dimension')}
                  options={dimensionOpts}
                  component={BngField}
                  clearable={false}
                  inputComponent={BngSelectSearch}
                />

                <div className={`${styles.secondHalfRow}`}>
                  <Field
                    name="cellFormatter"
                    rootClassName={`cellFormatter`}
                    label={t('cell.format')}
                    options={cellFormatOpts}
                    component={BngField}
                    inputComponent={BngSelectSearch}
                  />

                  <Field
                    name="visible"
                    rootClassName={`w-20`}
                    label={t('visible')}
                    component={BngField}
                    inputComponent={BngCheckbox}
                  />

                  <Field
                    name="dynamic"
                    rootClassName={`w-20`}
                    label={t('dynamic')}
                    component={BngField}
                    inputComponent={BngCheckbox}
                  />
                </div>
              </div>

              <Field
                name="formula"
                label={
                  <>
                    <span>{t('formula')}</span>
                    <Tooltip title={tooltipMeasures} className={styles.tooltip}>
                      <Icon icon="info_outline" className={styles.customIcon} />
                    </Tooltip>
                  </>
                }
                mode="text/x-sql"
                rootClassName={`${styles.textAreaFieldStyle}`}
                component={BngField}
                required={true}
                inputComponent={BngCodeMirror}
              />

              <div className={`${styles.blockWrapper} ${styles.thirdRow}`}>
                <Field
                  name="formatStringOptions"
                  inline={true}
                  label={t('format.string')}
                  options={formatStringOpts}
                  component={BngField}
                  inputComponent={BngSelectSearch}
                />
                <BngButton
                  className={styles.BngButton}
                  onClick={() => {
                    if (values.formatStringOptions) {
                      const selected = formatStringOpts.find((opt) => opt.value === values.formatStringOptions);
                      const isStandardOrPercent = ['Standard', 'Percent'].includes(selected?.value);

                      const finalFormatString = isStandardOrPercent ? selected.value : selected.alternative;
                      setValues({ ...values, formatString: finalFormatString, formatStringOptions: '' });
                    }
                  }}
                >
                  {t('apply')}
                </BngButton>
              </div>
              <Field
                name="formatString"
                label=""
                mode="text/x-sql"
                rootClassName={`${styles.formulaFormat} ${styles.textAreaFieldStyle}`}
                component={BngField}
                inputComponent={BngCodeMirror}
              />
            </Dialog.Body>

            <Dialog.Footer>
              <div className={`${styles.buttonsWrapper}`}>
                <BngIconButton
                  className={styles.validateMeasureBtn}
                  icon="check"
                  text={t('validate')}
                  onClick={async () => {
                    try {
                      const result = await Api.Origin.CalculatedMeasure.validateMeasure(inMemoryId, values);
                      if (result.success) {
                        UiMsg.ok(t('calculated.measure.valid.message'));
                        await Swal.fire({
                          html: `
                                  <center>
                                  <div style='text-align: center;'>
                                  <h4>Preview</h4>
                                  </div>
                                  <br/>
                                  <center>${result.response.replaceAll('\\', '')}</center>
                                  </div>
                            `,
                        });
                        setMeasureIsValid(true);
                      } else {
                        setMeasureIsValid(false);
                        UiMsg.error(t('calculated.measure.invalid.message'), result.response);
                      }
                    } catch (e) {
                      console.error('Error while validate measure!', { e });
                      UiMsg.error('', e);
                      setMeasureIsValid(false);
                    }
                  }}
                  disabled={_.isEmpty(values.formula)}
                />
                <BngIconButton className={styles.save} icon="save" text={t('save')} type="submit" disabled={!dirty} />
              </div>
            </Dialog.Footer>
          </BngForm>
        </Dialog>
      )}
    </Formik>
  );
}
